/**
 * Admin-related type definitions for trAInedup.ai
 * Defines interfaces for admin dashboard, analytics, content management, and moderation
 */

/**
 * Admin role types
 */
export enum AdminRole {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  MODERATOR = 'moderator',
  CONTENT_MANAGER = 'content_manager',
  SUPPORT = 'support',
}

/**
 * Analytics time periods
 */
export enum AnalyticsTimePeriod {
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  LAST_7_DAYS = 'last_7_days',
  LAST_30_DAYS = 'last_30_days',
  LAST_90_DAYS = 'last_90_days',
  LAST_YEAR = 'last_year',
  CUSTOM = 'custom',
}

/**
 * Content status types
 */
export enum ContentStatus {
  DRAFT = 'draft',
  PENDING_REVIEW = 'pending_review',
  APPROVED = 'approved',
  PUBLISHED = 'published',
  ARCHIVED = 'archived',
  REJECTED = 'rejected',
}

/**
 * User status types for admin management
 */
export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  BANNED = 'banned',
  PENDING_VERIFICATION = 'pending_verification',
}

/**
 * Dashboard analytics interface
 */
export interface DashboardAnalytics {
  // User metrics
  totalUsers: number;
  activeUsers: number;
  newUsers: number;
  userGrowthRate: number;
  
  // Course metrics
  totalCourses: number;
  publishedCourses: number;
  totalEnrollments: number;
  completionRate: number;
  
  // Revenue metrics
  totalRevenue: number;
  monthlyRecurringRevenue: number;
  averageRevenuePerUser: number;
  revenueGrowthRate: number;
  
  // Engagement metrics
  totalPosts: number;
  totalMessages: number;
  averageSessionDuration: number;
  dailyActiveUsers: number;
  
  // Support metrics
  totalTickets: number;
  openTickets: number;
  averageResponseTime: number;
  customerSatisfactionScore: number;
  
  // Time period
  period: AnalyticsTimePeriod;
  startDate: Date;
  endDate: Date;
  lastUpdated: Date;
}

/**
 * User analytics interface
 */
export interface UserAnalytics {
  userId: string;
  
  // Activity metrics
  totalSessions: number;
  totalSessionDuration: number;
  averageSessionDuration: number;
  lastActiveAt: Date;
  
  // Learning metrics
  coursesEnrolled: number;
  coursesCompleted: number;
  totalLearningTime: number;
  averageProgress: number;
  
  // Engagement metrics
  postsCreated: number;
  repliesCreated: number;
  messagesExchanged: number;
  likesReceived: number;
  
  // Subscription metrics
  subscriptionTier: string;
  subscriptionStartDate?: Date;
  totalSpent: number;
  
  // Geographic and device info
  country?: string;
  city?: string;
  deviceType: string;
  browserType: string;
  
  // Calculated metrics
  engagementScore: number;
  retentionScore: number;
  lifetimeValue: number;
  
  // Time period
  period: AnalyticsTimePeriod;
  calculatedAt: Date;
}

/**
 * Course analytics interface
 */
export interface CourseAnalytics {
  courseId: string;
  
  // Enrollment metrics
  totalEnrollments: number;
  activeEnrollments: number;
  completedEnrollments: number;
  dropoutRate: number;
  
  // Engagement metrics
  averageProgress: number;
  averageCompletionTime: number;
  totalWatchTime: number;
  averageRating: number;
  totalReviews: number;
  
  // Revenue metrics
  totalRevenue: number;
  averageRevenuePerEnrollment: number;
  
  // Content metrics
  totalLessons: number;
  totalDuration: number;
  mostPopularLessons: string[];
  leastPopularLessons: string[];
  
  // Performance metrics
  passRate: number;
  averageQuizScore: number;
  certificatesIssued: number;
  
  // Time-based metrics
  enrollmentsByMonth: { month: string; count: number }[];
  completionsByMonth: { month: string; count: number }[];
  
  // Period info
  period: AnalyticsTimePeriod;
  calculatedAt: Date;
}

/**
 * Content management interface
 */
export interface ContentItem {
  id: string;
  type: 'course' | 'lesson' | 'post' | 'page' | 'resource';
  title: string;
  description?: string;
  content?: string;
  
  // Status and workflow
  status: ContentStatus;
  version: number;
  
  // Author and review info
  authorId: string;
  authorName: string;
  reviewerId?: string;
  reviewerName?: string;
  reviewNotes?: string;
  
  // Publishing info
  publishedAt?: Date;
  scheduledPublishAt?: Date;
  expiresAt?: Date;
  
  // SEO and metadata
  slug?: string;
  tags: string[];
  categories: string[];
  metaTitle?: string;
  metaDescription?: string;
  
  // Media and assets
  featuredImage?: string;
  attachments: ContentAttachment[];
  
  // Analytics
  views: number;
  likes: number;
  shares: number;
  comments: number;
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Content attachment interface
 */
export interface ContentAttachment {
  id: string;
  type: 'image' | 'video' | 'audio' | 'document' | 'archive';
  filename: string;
  originalFilename: string;
  url: string;
  size: number;
  mimeType: string;
  
  // Media-specific properties
  duration?: number; // for video/audio
  dimensions?: { width: number; height: number }; // for images/videos
  thumbnail?: string;
  
  // Metadata
  uploadedBy: string;
  uploadedAt: Date;
}

/**
 * User management interface for admin
 */
export interface AdminUserProfile {
  id: string;
  email: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  
  // Account status
  status: UserStatus;
  emailVerified: boolean;
  phoneVerified: boolean;
  
  // Roles and permissions
  roles: string[];
  permissions: string[];
  isAffiliate?: boolean;

  // Signup source
  acquisitionSource?: string;
  intendedDestination?: string;

  // Subscription info
  subscriptionTier: string;
  subscriptionStatus: string;
  subscriptionStartDate?: Date;
  subscriptionEndDate?: Date;

  // Activity info
  lastLoginAt?: Date;
  lastActiveAt?: Date;
  loginCount: number;
  
  // Learning progress
  coursesEnrolled: number;
  coursesCompleted: number;
  totalLearningTime: number;
  skillsAssessmentCompleted?: boolean;
  skillsAssessmentCompletedAt?: Date;
  
  // Community activity
  postsCount: number;
  repliesCount: number;
  likesReceived: number;
  reputation: number;
  
  // Moderation info
  warningsCount: number;
  suspensionsCount: number;
  lastModerationAction?: Date;
  moderationNotes?: string;
  
  // Geographic and device info
  country?: string;
  city?: string;
  timezone?: string;
  preferredLanguage: string;
  
  // Account timestamps
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Moderation action interface
 */
export interface ModerationAction {
  id: string;
  type: 'warning' | 'suspension' | 'ban' | 'content_removal' | 'account_restriction';
  targetUserId?: string;
  targetContentId?: string;
  targetContentType?: string;
  
  // Action details
  reason: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  
  // Duration (for temporary actions)
  duration?: number; // in hours
  expiresAt?: Date;
  
  // Moderator info
  moderatorId: string;
  moderatorName: string;
  
  // Status
  status: 'active' | 'expired' | 'revoked';
  revokedBy?: string;
  revokedAt?: Date;
  revokeReason?: string;
  
  // Appeal info
  appealSubmitted: boolean;
  appealedAt?: Date;
  appealReason?: string;
  appealStatus?: 'pending' | 'approved' | 'rejected';
  appealReviewedBy?: string;
  appealReviewedAt?: Date;
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
}

/**
 * System settings interface
 */
export interface SystemSettings {
  id: string;
  category: string;
  key: string;
  value: any;
  type: 'string' | 'number' | 'boolean' | 'json' | 'array';
  
  // Metadata
  title: string;
  description: string;
  isPublic: boolean;
  isEditable: boolean;
  
  // Validation
  validationRules?: {
    required?: boolean;
    min?: number;
    max?: number;
    pattern?: string;
    options?: string[];
  };
  
  // Change tracking
  lastModifiedBy: string;
  lastModifiedAt: Date;
  changeHistory: SettingChange[];
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Setting change history interface
 */
export interface SettingChange {
  id: string;
  oldValue: any;
  newValue: any;
  changedBy: string;
  changedAt: Date;
  reason?: string;
}

/**
 * Support ticket interface
 */
export interface SupportTicket {
  id: string;
  ticketNumber: string;
  
  // User info
  userId?: string;
  userEmail: string;
  userName: string;
  
  // Ticket details
  subject: string;
  description: string;
  category: 'technical' | 'billing' | 'account' | 'course' | 'community' | 'other';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'waiting_for_user' | 'resolved' | 'closed';
  
  // Assignment
  assignedTo?: string;
  assignedAt?: Date;
  
  // Resolution
  resolution?: string;
  resolvedBy?: string;
  resolvedAt?: Date;
  
  // Satisfaction
  satisfactionRating?: number;
  satisfactionFeedback?: string;
  
  // Attachments and messages
  attachments: SupportAttachment[];
  messages: SupportMessage[];
  
  // Tags and labels
  tags: string[];
  labels: string[];
  
  // SLA tracking
  firstResponseAt?: Date;
  firstResponseTime?: number; // in minutes
  resolutionTime?: number; // in minutes
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Support ticket attachment interface
 */
export interface SupportAttachment {
  id: string;
  filename: string;
  url: string;
  size: number;
  mimeType: string;
  uploadedBy: string;
  uploadedAt: Date;
}

/**
 * Support ticket message interface
 */
export interface SupportMessage {
  id: string;
  ticketId: string;
  senderId: string;
  senderName: string;
  senderType: 'user' | 'agent' | 'system';
  
  // Message content
  content: string;
  isInternal: boolean; // internal notes not visible to user
  
  // Attachments
  attachments: SupportAttachment[];
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Audit log interface
 */
export interface AuditLog {
  id: string;
  
  // Action details
  action: string;
  resource: string;
  resourceId?: string;
  
  // User info
  userId?: string;
  userEmail?: string;
  userRole?: string;
  
  // Request details
  ipAddress: string;
  userAgent: string;
  
  // Changes
  oldValues?: Record<string, any>;
  newValues?: Record<string, any>;
  
  // Context
  context?: Record<string, any>;
  
  // Result
  success: boolean;
  errorMessage?: string;
  
  // Timestamp
  createdAt: Date;
}

/**
 * Platform statistics interface
 */
export interface PlatformStats {
  // User statistics
  totalUsers: number;
  activeUsers: number;
  newUsersToday: number;
  userRetentionRate: number;
  
  // Content statistics
  totalCourses: number;
  totalLessons: number;
  totalPosts: number;
  totalMessages: number;
  
  // Engagement statistics
  dailyActiveUsers: number;
  weeklyActiveUsers: number;
  monthlyActiveUsers: number;
  averageSessionDuration: number;
  
  // Revenue statistics
  totalRevenue: number;
  monthlyRevenue: number;
  averageRevenuePerUser: number;
  
  // Support statistics
  openTickets: number;
  averageResponseTime: number;
  customerSatisfactionScore: number;
  
  // System health
  systemUptime: number;
  systemResponseTime: number;
  errorRate: number;
  
  // Last updated
  lastUpdated: Date;
}

/**
 * Email status types
 */
export enum EmailStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  RETRY = 'RETRY',
}

/**
 * Email record interface
 */
export interface EmailRecord {
  id: string;
  to: string[];
  from: string;
  subject: string;
  templateId?: string | null;
  status: EmailStatus;
  error?: string | null;
  createdAt: Date | null;
  sentAt: Date | null;
  attempts: number;
  isManual: boolean;
  isScheduled?: boolean;
  isResend?: boolean;
  originalEmailId?: string;
  resendHistory?: Array<{
    emailId: string;
    resentAt: Date;
    resentBy: string;
  }>;
  sentBy?: {
    userId: string;
    name: string;
    email: string | null;
    timestamp?: Date;
  } | null;
  flowType?: string | null;
  flowStep?: number | null;
}

/**
 * Email details interface (full email data)
 */
export interface EmailDetails extends EmailRecord {
  htmlBody: string;
  textBody: string;
  templateData?: any | null;
  info?: any | null;
}

/**
 * Email statistics interface
 */
export interface EmailStats {
  totalEmails: number;
  successCount: number;
  pendingCount: number;
  errorCount: number;
  manualCount: number;
  automatedCount: number;
  flowTypeBreakdown?: Record<string, number>;
  successRate: string;
  period: string;
  startDate: string;
  endDate: string;
}

/**
 * Email recipient interface
 */
export interface EmailRecipient {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  subscriptionTier: string;
  emailVerified: boolean;
}

/**
 * Bulk operation interface
 */
export interface BulkOperation {
  id: string;
  type: 'user_update' | 'content_update' | 'email_send' | 'data_export' | 'data_import';
  
  // Operation details
  title: string;
  description: string;
  targetCount: number;
  
  // Progress tracking
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: number; // 0-100
  processedCount: number;
  successCount: number;
  failureCount: number;
  
  // Results
  results?: any[];
  errors?: string[];
  
  // Execution info
  startedBy: string;
  startedAt?: Date;
  completedAt?: Date;
  estimatedDuration?: number;
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
}

const adminTypes = {
  AdminRole,
  AnalyticsTimePeriod,
  ContentStatus,
  UserStatus,
  EmailStatus,
};

export default adminTypes;
